// A sampled ground field over every island: height, distance to the cobble path, grass
// greenness and grass density. Shared by the terrain colouring and the grass shader
// (uploaded as one half-float texture), so grass colour always matches the ground under it.

import * as THREE from 'three';
import { World, NO_GROUND } from '../../../sim/world';
import { valueNoise, fbm, smoothstep } from '../../../sim/core';
import { Clock, type Pause } from '../warm';

export class GroundField {
  readonly x0: number; readonly z0: number; readonly res: number;
  readonly nx: number; readonly nz: number;
  readonly h: Float32Array;
  /** signed distance to the path edge (negative = on the path) */
  readonly pd: Float32Array;
  readonly green: Float32Array;
  readonly dens: Float32Array;
  readonly world: World;

  /** Empty until fillSteps() has been stepped through (the island build does it in slices). */
  constructor(world: World, res: number) {
    this.world = world;
    let x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
    for (const isl of world.islands) {
      const r = isl.radius * 1.06 + 2;
      x0 = Math.min(x0, isl.cx - r); x1 = Math.max(x1, isl.cx + r);
      z0 = Math.min(z0, isl.cz - r); z1 = Math.max(z1, isl.cz + r);
    }
    this.res = res; this.x0 = x0; this.z0 = z0;
    this.nx = Math.ceil((x1 - x0) / res) + 1;
    this.nz = Math.ceil((z1 - z0) / res) + 1;
    const n = this.nx * this.nz;
    this.h = new Float32Array(n);
    this.pd = new Float32Array(n).fill(1e3);
    this.green = new Float32Array(n);
    this.dens = new Float32Array(n);
  }

  /** Half-width of the cobble path at x: narrow trail, wide main street through the town. */
  pathHalfWidth(x: number, z: number) {
    const t = this.world.town;
    const inTown = smoothstep(t.x0 - 9, t.x0 - 2, x) * smoothstep(t.x1 + 7, t.x1 + 1, x) * smoothstep(12, 4, Math.abs(z - t.z));
    return 1.55 + (4.5 - 1.55) * inTown;
  }

  greenAt(x: number, z: number) {
    const s = this.world.seed;
    const g = smoothstep(0.5, 0.7, valueNoise(s ^ 0x9a1, x / 26, z / 26));
    const g2 = smoothstep(0.62, 0.8, valueNoise(s ^ 0x9a2, x / 9, z / 9)) * 0.45;
    return Math.min(1, g * 0.7 + g2);
  }
  /** Sienna drift patches (rusty, darker grass), 0..1. */
  siennaAt(x: number, z: number) {
    const s = this.world.seed;
    return smoothstep(0.5, 0.68, valueNoise(s ^ 0x51e, x / 19, z / 19)) * (0.6 + 0.4 * valueNoise(s ^ 0x51f, x / 6, z / 6));
  }
  /** Signed drift: + greener patches, - sienna patches (what the grass and ground both read). */
  driftAt(x: number, z: number) {
    return this.greenAt(x, z) - this.siennaAt(x, z);
  }
  dirtAt(x: number, z: number) {
    return smoothstep(0.64, 0.75, fbm(this.world.seed ^ 0x77, x / 15, z / 15, 3));
  }

  idx(x: number, z: number) {
    const i = Math.round((x - this.x0) / this.res), j = Math.round((z - this.z0) / this.res);
    if (i < 0 || j < 0 || i >= this.nx || j >= this.nz) return -1;
    return j * this.nx + i;
  }
  /** Unit ground normal from the sampled heights (central differences), written into out. */
  normalAt(x: number, z: number, out: number[]) {
    const r = this.res;
    const h0 = this.hAt(x, z);
    const hl = this.hAt(x - r, z, h0), hr = this.hAt(x + r, z, h0), hd = this.hAt(x, z - r, h0), hu = this.hAt(x, z + r, h0);
    const nx = hl - hr, nz = hd - hu, ny = 2 * r;
    const l = Math.sqrt(nx * nx + ny * ny + nz * nz);
    out[0] = nx / l; out[1] = ny / l; out[2] = nz / l;
    return out;
  }
  /** Bilinear height from the field (fallback where there's no ground). */
  hAt(x: number, z: number, fallback = 0) {
    const fx = (x - this.x0) / this.res, fz = (z - this.z0) / this.res;
    const i = Math.floor(fx), j = Math.floor(fz);
    if (i < 0 || j < 0 || i >= this.nx - 1 || j >= this.nz - 1) return fallback;
    const tx = fx - i, tz = fz - j, k = j * this.nx + i;
    const a = this.h[k], b = this.h[k + 1], c = this.h[k + this.nx], d = this.h[k + this.nx + 1];
    if (a === NO_GROUND || b === NO_GROUND || c === NO_GROUND || d === NO_GROUND) {
      const w = this.world.terrain(x, z);
      return w === NO_GROUND ? fallback : w;
    }
    return (a * (1 - tx) + b * tx) * (1 - tz) + (c * (1 - tx) + d * tx) * tz;
  }
  pathDist(x: number, z: number) { const k = this.idx(x, z); return k < 0 ? 1e3 : this.pd[k]; }

  /** The fill in slices (a background island build: client/render/warm.ts). */
  *fillSteps(pause: Pause): Generator<unknown, void, unknown> {
    const w = this.world, { nx, nz, res } = this;
    const clk = new Clock(pause);
    for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
      if (i === 0) yield* clk.tick('field');
      const x = this.x0 + i * res, z = this.z0 + j * res, k = j * nx + i;
      const h = w.terrain(x, z);
      this.h[k] = h === NO_GROUND ? NO_GROUND : h;
      this.green[k] = this.driftAt(x, z);
    }
    // path distance (minus local half width) by rasterising each segment
    const P = w.path;
    for (let s = 0; s < P.length - 1; s++) {
      yield* clk.tick('field');
      const a = P[s], b = P[s + 1];
      const hw = Math.max(this.pathHalfWidth(a.x, a.z), this.pathHalfWidth(b.x, b.z));
      const pad = hw + 4;
      const i0 = Math.max(0, Math.floor((Math.min(a.x, b.x) - pad - this.x0) / res));
      const i1 = Math.min(nx - 1, Math.ceil((Math.max(a.x, b.x) + pad - this.x0) / res));
      const j0 = Math.max(0, Math.floor((Math.min(a.z, b.z) - pad - this.z0) / res));
      const j1 = Math.min(nz - 1, Math.ceil((Math.max(a.z, b.z) + pad - this.z0) / res));
      const abx = b.x - a.x, abz = b.z - a.z, ab2 = abx * abx + abz * abz || 1;
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
        const x = this.x0 + i * res, z = this.z0 + j * res;
        let t = ((x - a.x) * abx + (z - a.z) * abz) / ab2;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const qx = a.x + abx * t - x, qz = a.z + abz * t - z;
        const d = Math.sqrt(qx * qx + qz * qz) - this.pathHalfWidth(a.x + abx * t, a.z + abz * t);
        const k = j * nx + i;
        if (d < this.pd[k]) this.pd[k] = d;
      }
    }
    // grass density
    const t = w.town;
    for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
      if (i === 0) yield* clk.tick('field');
      const k = j * nx + i;
      const h = this.h[k];
      if (h === NO_GROUND) { this.dens[k] = 0; continue; }
      const x = this.x0 + i * res, z = this.z0 + j * res;
      // slope from neighbours
      const hl = this.h[k - 1] ?? h, hr = this.h[k + 1] ?? h, hd = this.h[k - nx] ?? h, hu = this.h[k + nx] ?? h;
      const ok = (v: number) => (v === NO_GROUND ? h - 2 : v);
      const gx = (ok(hr) - ok(hl)) / (2 * res), gz = (ok(hu) - ok(hd)) / (2 * res);
      const ny = 1 / Math.sqrt(1 + gx * gx + gz * gz);
      let d = smoothstep(0.7, 0.85, ny);
      // edge: no grass on the last metre before the cliff
      if (ok(hl) < h - 1.5 || ok(hr) < h - 1.5 || ok(hd) < h - 1.5 || ok(hu) < h - 1.5) d = 0;
      d *= smoothstep(0.1, 0.9, this.pd[k]);
      // no grass in the water (and only a thin fringe on the banks)
      if (w.waterAt(x, z) !== NO_GROUND || !w.dryAt(x, z, -2.2)) d = 0;
      d *= 1 - this.dirtAt(x, z) * 0.85;
      const tm = w.townMask(x, z);
      if (tm > 0) {
        d *= 1 - tm * 0.55;
        // the town street + boardwalks (drawn by town_build.ts street(): x0-5 .. x1+3) carry no grass
        if (x > t.x0 - 5 && x < t.x1 + 3 && Math.abs(z - t.z) < t.halfWidth + 2.6) d = 0;
      }
      this.dens[k] = d;
    }
    // obstacle footprints
    const clearRect = (cx: number, cz: number, hx: number, hz: number) => {
      const i0 = Math.max(0, Math.floor((cx - hx - this.x0) / res)), i1 = Math.min(nx - 1, Math.ceil((cx + hx - this.x0) / res));
      const j0 = Math.max(0, Math.floor((cz - hz - this.z0) / res)), j1 = Math.min(nz - 1, Math.ceil((cz + hz - this.z0) / res));
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
        const x = this.x0 + i * res, z = this.z0 + j * res;
        if (Math.abs(x - cx) <= hx && Math.abs(z - cz) <= hz) this.dens[j * nx + i] = 0;
      }
    };
    const clearDisc = (cx: number, cz: number, r: number) => {
      const i0 = Math.max(0, Math.floor((cx - r - this.x0) / res)), i1 = Math.min(nx - 1, Math.ceil((cx + r - this.x0) / res));
      const j0 = Math.max(0, Math.floor((cz - r - this.z0) / res)), j1 = Math.min(nz - 1, Math.ceil((cz + r - this.z0) / res));
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
        const x = this.x0 + i * res - cx, z = this.z0 + j * res - cz;
        if (x * x + z * z <= r * r) this.dens[j * nx + i] *= Math.min(1, Math.max(0, (Math.sqrt(x * x + z * z) - r * 0.85) / (r * 0.15)));
      }
    };
    // (floating things don't shade the grass out: shelves, keels and arch spans hang above it)
    for (const b of w.boxes) if (b.kind !== 'arch') clearRect(b.x, b.z, b.hx + 0.3, b.hz + 0.3);
    for (const c of w.cyls) if (c.kind !== 'shelf' && c.kind !== 'keel') clearDisc(c.x, c.z, c.r + 0.35);
    clearDisc(w.gate.x, w.gate.z, 4.5);
  }

  /** RGBA half-float texture: r height, g density, b signed drift (+green / -sienna), a path distance. */
  texture(): THREE.DataTexture {
    const n = this.nx * this.nz;
    const data = new Uint16Array(n * 4);
    const f = THREE.DataUtils.toHalfFloat;
    for (let k = 0; k < n; k++) {
      const h = this.h[k];
      data[k * 4] = f(h === NO_GROUND ? -60 : h);
      data[k * 4 + 1] = f(this.dens[k]);
      data[k * 4 + 2] = f(this.green[k]);
      data[k * 4 + 3] = f(Math.min(this.pd[k], 50));
    }
    const tex = new THREE.DataTexture(data, this.nx, this.nz, THREE.RGBAFormat, THREE.HalfFloatType);
    tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter;
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;
    return tex;
  }
}
